import { FC, useState, useEffect } from "react"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Loader2 } from "lucide-react"
import { updateDocumentRelevancy } from "@/api/documents"
import { useToast } from "@/components/ui/use-toast"
import { useQueryClient } from "@tanstack/react-query"

interface UpdateRelevancyModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	documentId: string
	caseId: string
	documentName: string
	currentIsRelevant: boolean | null
}

export const UpdateRelevancyModal: FC<UpdateRelevancyModalProps> = ({
	open,
	onOpenChange,
	documentId,
	caseId,
	documentName,
	currentIsRelevant,
}) => {
	const { toast } = useToast()
	const queryClient = useQueryClient()
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [isRelevant, setIsRelevant] = useState<boolean>(
		currentIsRelevant === true
	)
	const [reason, setReason] = useState("")

	// Reset form state when modal opens or currentIsRelevant changes
	useEffect(() => {
		if (open) {
			// Set the initial state to the current document's relevancy status
			setIsRelevant(currentIsRelevant === true)
			setReason("")
		}
	}, [open, currentIsRelevant])

	const resetForm = () => {
		setReason("")
		setIsRelevant(currentIsRelevant === true)
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		setIsSubmitting(true)
		
		try {
			await updateDocumentRelevancy(caseId, documentId, isRelevant, reason)
			
			// Invalidate the document stages query to refresh the data
			queryClient.invalidateQueries({
				queryKey: ["documentStages", caseId, documentId],
			})
			
			toast({
				title: "Relevancy updated",
				description: `Document has been marked as ${isRelevant ? "relevant" : "irrelevant"}.`,
			})
			
			// Reset form before closing modal
			resetForm()
			onOpenChange(false)
		} catch (error) {
			console.error("Error updating relevancy:", error)
			toast({
				title: "Error updating relevancy",
				description: "An error occurred while updating the document relevancy.",
				variant: "destructive",
			})
		} finally {
			setIsSubmitting(false)
		}
	}

	// Prevent event propagation from the form to parent components
	const handleFormKeyDown = (e: React.KeyboardEvent) => {
		e.stopPropagation()
	}

	return (
		<Dialog 
			open={open} 
			onOpenChange={(newOpen) => {
				if (!newOpen && !isSubmitting) {
					resetForm()
				}
				onOpenChange(newOpen)
			}}
		>
			<DialogContent className="sm:max-w-[425px]" onKeyDown={handleFormKeyDown}>
				<DialogHeader>
					<DialogTitle>Update Document Relevancy</DialogTitle>
					<DialogDescription>
						Change the relevancy status of "{documentName}" and optionally provide a reason
					</DialogDescription>
				</DialogHeader>

				<form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
					<div className="grid gap-4 py-4">
						<div className="grid grid-cols-1 gap-2">
							<div className="space-y-2">
								<Label htmlFor="relevancy">Relevancy Status</Label>
								<div className="flex space-x-4">
									<Label
										htmlFor="relevant"
										className="flex items-center space-x-2 cursor-pointer"
									>
										<Input
											id="relevant"
											type="radio"
											name="relevancy"
											value="relevant"
											checked={isRelevant}
											onChange={() => setIsRelevant(true)}
											className="h-4 w-4"
										/>
										<span>Relevant</span>
									</Label>
									<Label
										htmlFor="irrelevant"
										className="flex items-center space-x-2 cursor-pointer"
									>
										<Input
											id="irrelevant"
											type="radio"
											name="relevancy"
											value="irrelevant"
											checked={!isRelevant}
											onChange={() => setIsRelevant(false)}
											className="h-4 w-4"
										/>
										<span>Irrelevant</span>
									</Label>
								</div>
							</div>

							<div className="space-y-2">
								<Label htmlFor="reason">
									Reason{" "}
									<span className="text-xs text-muted-foreground">
										(optional)
									</span>
								</Label>
								<Textarea
									id="reason"
									value={reason}
									onChange={(e) => setReason(e.target.value)}
									placeholder="Provide a reason for updating the relevancy status (optional)"
									className=""
									onClick={(e) => e.stopPropagation()}
								/>
							</div>
						</div>
					</div>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={(e) => {
								e.stopPropagation();
								resetForm();
								onOpenChange(false);
							}}
							disabled={isSubmitting}
						>
							Cancel
						</Button>
						<Button 
							type="submit" 
							disabled={isSubmitting}
							onClick={(e) => e.stopPropagation()}
						>
							{isSubmitting && (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							)}
							Save Changes
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	)
} 